import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';
import { Invoice, CompanyConfig } from '../types';
import { 
  calculateTotals, 
  generateRandomCustomer, 
  generateOrderNumber, 
  generateInvoiceNumber, 
  generateTrackingNumber,
  formatDate 
} from './generators';

export const generateInvoicePDF = async (invoices: Invoice[]): Promise<void> => {
  const doc = new jsPDF('p', 'mm', 'a4'); // Portrait, millimeters, A4
  
  for (let i = 0; i < invoices.length; i++) {
    if (i > 0) {
      doc.addPage();
    }
    
    await drawInvoicePage(doc, invoices[i]);
  }
  
  // Save the PDF
  const fileName = `invoices_${invoices[0].orderDate.replace(/\./g, '')}_${invoices.length}pages.pdf`;
  doc.save(fileName);
};

// New function to generate PDF with company-specific page counts
export const generateMultiCompanyInvoicePDF = async (
  companyConfigs: CompanyConfig[]
): Promise<void> => {
  const doc = new jsPDF('p', 'mm', 'a4'); // Portrait, millimeters, A4
  
  let totalPages = 0;
  let isFirstPage = true;
  
  // Generate pages for each company
  for (const config of companyConfigs) {
    for (let pageNum = 1; pageNum <= config.pageCount; pageNum++) {
      if (!isFirstPage) {
        doc.addPage();
      }
      isFirstPage = false;
      
      // Create a modified invoice for this company and page
      const customer = generateRandomCustomer();
      const orderNumber = generateOrderNumber();
      const invoiceNumber = generateInvoiceNumber();
      const trackingNumber = generateTrackingNumber();
      const currentDate = formatDate(new Date());

      const companyInvoice: Invoice = {
        orderNumber,
        invoiceNumber,
        trackingNumber,
        customer,
        company: config.company,
        items: config.items,
        orderDate: currentDate,
        invoiceDate: currentDate,
        deliveryType: config.deliveryType
      };
      
      await drawInvoicePage(doc, companyInvoice);
      totalPages++;
    }
  }
  
  // Save the PDF with total page count
  const fileName = `multi_company_invoices_${new Date().toISOString().split('T')[0]}_${totalPages}pages.pdf`;
  doc.save(fileName);
};

// Helper function to wrap text naturally within a given width
const wrapText = (doc: jsPDF, text: string, x: number, y: number, maxWidth: number, lineHeight: number): number => {
  const words = text.split(' ');
  let line = '';
  let currentY = y;
  
  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i] + ' ';
    const testWidth = doc.getTextWidth(testLine);
    
    if (testWidth > maxWidth && line !== '') {
      doc.text(line.trim(), x, currentY);
      line = words[i] + ' ';
      currentY += lineHeight;
    } else {
      line = testLine;
    }
  }
  
  if (line.trim() !== '') {
    doc.text(line.trim(), x, currentY);
    currentY += lineHeight;
  }
  
  return currentY;
};

const drawInvoicePage = async (doc: jsPDF, invoice: Invoice): Promise<void> => {
  // Generate QR Code exactly like in image
  const qrCodeData = `Order: ${invoice.orderNumber}\nTracking: ${invoice.trackingNumber}\nCustomer: ${invoice.customer.name}`;
  const qrCodeDataURL = await QRCode.toDataURL(qrCodeData, { 
    width: 200,
    margin: 1,
    color: { dark: '#000000', light: '#FFFFFF' }
  });
  
  // Generate barcode exactly like in image
  const canvas = document.createElement('canvas');
  JsBarcode(canvas, invoice.trackingNumber, {
    format: "CODE128",
    width: 3,        // Thick bars exactly like image
    height: 40,      // Reduced height for compact barcode like image
    displayValue: false,
    margin: 0
  });
  const barcodeDataURL = canvas.toDataURL();
  
  // Set document properties - ALL BORDERS 1px
  const borderWidth = 1 / 2.83465; // 1px = 0.352778mm (1px / 2.83465 = mm)
  doc.setLineWidth(borderWidth);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  
  // ===== EXACT LAYOUT FROM IMAGE =====
  
  // LEFT SIDE: Customer Address Block (Top Left)
  doc.setLineWidth(borderWidth); // 1px border
  doc.rect(15, 10, 90, 85); // Adjusted with better margins: x=15mm, width=90mm
  
  // Customer Address section - Section titles
  doc.setFontSize(10); // Font size 10 as specified
  doc.setFont('helvetica', 'bold');
  doc.text('Customer Address', 17, 18);
  
  // Customer name - Font size 12 as specified
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12); // Font size 12 as specified
  doc.text(invoice.customer.name, 17, 23);
  
  // Customer address - Font size 11 as specified
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11); // Font size 11 as specified
  let yPos = 27;
  
  // Parse address to extract city and state for last line
  const addressParts = invoice.customer.address.split(',').map(part => part.trim());
  const mainAddress = addressParts.slice(0, -2).join(', '); // All except last 2 parts
  const city = addressParts[addressParts.length - 2] || '';
  const state = addressParts[addressParts.length - 1] || '';
  
  // Wrap main address
  if (mainAddress) {
    yPos = wrapText(doc, mainAddress, 17, yPos, 80, 4);
  }
  
  // Last line: City, State, Pincode
  doc.text(`${city}, ${state}, ${invoice.customer.pincode}`, 17, yPos);
  
  // Horizontal divider - positioned for balanced sections like Image 2
  doc.setLineWidth(borderWidth); // 1px border
  doc.line(15, 45, 105, 45);
  
  // Return Address section - Font size 10 as specified
  doc.setFontSize(10); // Font size 10 as specified
  doc.setFont('helvetica', 'bold');
  doc.text('If undelivered, return to:', 17, 50);
  
  // Format return address with separate lines like image
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10); // Font size 10 as specified
  yPos = 54;
  
  // Company name first - use storeName if available, otherwise company name
  const displayStoreName = invoice.company.storeName || invoice.company.name;
  doc.text(displayStoreName, 17, yPos);
  yPos += 4;
  
  // Extract pincode from company address first
  const returnPincodeMatch = invoice.company.address.match(/\b\d{6}\b/);
  const returnPincode = returnPincodeMatch ? returnPincodeMatch[0] : '470001';
  
  // Remove pincode from address to avoid duplication
  const addressWithoutPincode = invoice.company.address.replace(/\b\d{6}\b/, '').trim();
  
  // Parse company address to extract city, state
  const companyAddressParts = addressWithoutPincode.split(',').map(part => part.trim()).filter(part => part);
  const companyMainAddress = companyAddressParts.slice(0, -2).join(', '); // All except last 2 parts
  const companyCity = companyAddressParts[companyAddressParts.length - 2] || 'sagar';
  const companyState = companyAddressParts[companyAddressParts.length - 1] || 'Madhya Pradesh';
  
  // Wrap main address (already without pincode)
  if (companyMainAddress) {
    yPos = wrapText(doc, companyMainAddress, 17, yPos, 80, 4);
  }
  
  // Last line: City, State, Pincode
  doc.text(`${companyCity}, ${companyState}, ${returnPincode}`, 17, yPos);
  
  // RIGHT SIDE: Delhivery barcode section (top right) - starts immediately after left section
  
  // Payment Header - Black background (dynamic based on delivery type)
  doc.setFillColor(0, 0, 0);
  doc.rect(105, 10, 90, 8, 'F'); // Adjusted width to 90mm for better margins
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14); // Font size 14 as specified
  doc.setFont('helvetica', 'bold');
  
  // Dynamic text based on delivery type
  const headerText = invoice.deliveryType === 'COD' 
    ? 'COD: Check the payable amount on the app'
    : 'Prepaid: Do not collect cash';
  doc.text(headerText, 107, 16); // Adjusted x position
  doc.setTextColor(0, 0, 0);
  
  // Delivery section - Courier name
  doc.setFontSize(16); // Font size 16 as specified
  doc.setFont('helvetica', 'bold');
  doc.text('Delhivery', 107, 28); // Changed to "Delhivery" to match image exactly
  
  // Pickup badge
  doc.setFillColor(0, 0, 0);
  doc.rect(107, 30, 16, 5, 'F'); // Adjusted x position
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10); // Font size 10 as specified
  doc.setFont('helvetica', 'bold');
  doc.text('Pickup', 109, 34); // Adjusted x position
  doc.setTextColor(0, 0, 0);
  
  // Destination Code and Return Code labels (normal font, not bold)
  doc.setFontSize(10); // Font size 10 as specified
  doc.setFont('helvetica', 'normal');
  doc.text('Destination Code', 107, 40); // Normal font
  
  // Space between Destination Code and Return Code
  doc.text('Return Code', 107, 48); // Added proper spacing (8mm gap instead of 5mm)
  
  // Return code value: pincode from company address, company ID
  const pincodeMatch = invoice.company.address.match(/\b\d{6}\b/);
  const companyPincode = pincodeMatch ? pincodeMatch[0] : '470001'; // Fallback to 470001
  const companyIdPart = invoice.company.companyId || Math.floor(Math.random() * 9000000 + 1000000).toString(); // Use company ID or generate random
  const returnCode = `${companyPincode},${companyIdPart}`;
  doc.setFont('helvetica', 'bold'); // Return code value is bold
  doc.setFontSize(10); // Font size 10 as specified
  doc.text(returnCode, 107, 53); // Return code value below the label
  
  // QR Code (top right) - bigger and moved down
  doc.addImage(qrCodeDataURL, 'PNG', 163, 25, 30, 30); // Increased size from 25x25 to 30x30, moved down from y=20 to y=25
  
  // Tracking Number (center, above barcode) - centered with barcode, normal font
  doc.setFontSize(16); // Font size 16 as specified
  doc.setFont('helvetica', 'normal'); // Changed to normal
  const trackingNumberWidth = doc.getTextWidth(invoice.trackingNumber);
  const barcodeX = 110;
  const barcodeWidth = 80;
  const barcodeCenterX = barcodeX + (barcodeWidth / 2) - (trackingNumberWidth / 2);
  doc.text(invoice.trackingNumber, barcodeCenterX, 68); // Centered above barcode
  
  // Barcode (center, compact and properly positioned like image)
  doc.addImage(barcodeDataURL, 'PNG', 110, 71, 80, 14); // Moved down for better spacing
  
  // ===== PRODUCT DETAILS TABLE (POSITIONED AFTER BARCODE WITH PROPER SPACING) =====
  
  // Product Details box - proper spacing like image
  doc.setLineWidth(borderWidth); // 1px border
  doc.rect(15, 95, 180, 18); // Adjusted with better margins: x=15mm, width=180mm
  
  // Product Details title
  doc.setFontSize(11); // Font size 11 as specified
  doc.setFont('helvetica', 'bold');
  doc.text('Product Details', 17, 101);
  
  // Table headers - Product Details Table with evenly distributed columns like Image 1
  doc.setFontSize(11); // Changed to 11pt Bold
  doc.setFont('helvetica', 'bold');
  doc.text('SKU', 17, 106);
  doc.text('Size', 70, 106);  // Evenly spaced
  doc.text('Qty', 105, 106);  // Evenly spaced
  doc.text('Color', 130, 106); // Evenly spaced
  doc.text('Order No.', 148, 106); // Moved more left to stay inside border
  
  // Product data - Table body text with normal font size
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11); // Changed to 11pt Normal
  const productItem = invoice.items[0];
  doc.text(productItem.product.sku, 17, 110);
  doc.text(productItem.size, 70, 110);
  doc.text(productItem.quantity.toString(), 105, 110);
  doc.text(productItem.color, 130, 110);
  // Add _1 suffix to order number in Product Details section
  const orderNumberWithSuffix = `${invoice.orderNumber}_1`;
  doc.text(orderNumberWithSuffix, 148, 110); // Moved more left to stay inside border
  
  // ===== TAX INVOICE SECTION (NO GAP - DIRECTLY AFTER PRODUCT DETAILS) =====
  
  // TAX INVOICE section border - starts immediately after Product Details
  doc.setLineWidth(borderWidth); // 1px border
  doc.setDrawColor(0, 0, 0);
  doc.rect(15, 113, 180, 48); // Adjusted with better margins: x=15mm, width=180mm
  
  // TAX INVOICE header row with gray background - thinner like in images
  doc.setFillColor(220, 220, 220); // Light gray background like images
  doc.rect(15, 113, 180, 8, 'F'); // Adjusted with better margins
  
  doc.setFillColor(255, 255, 255); // Reset to white
  // TAX INVOICE title - reduced size
  doc.setFontSize(10); // Font size 10 as specified
  doc.setFont('helvetica', 'bold');
  const taxInvoiceWidth = doc.getTextWidth('TAX INVOICE');
  const taxInvoiceCenterX = (210 - taxInvoiceWidth) / 2;
  doc.text('TAX INVOICE', taxInvoiceCenterX, 118);
  
  // Original for Recipient note - bold
  doc.setFontSize(8); // Font size 8 as specified
  doc.setFont('helvetica', 'bold'); // Changed to bold
  doc.text('Original For Recipient', 165, 118); // Adjusted for better margins
  
  // Horizontal line below TAX INVOICE text
  doc.setLineWidth(borderWidth); // 1px border
  doc.line(15, 121, 195, 121); // Adjusted with better margins
  
  // Explicit left and right borders for TAX INVOICE header section
  doc.line(15, 113, 15, 121); // Left border
  doc.line(195, 113, 195, 121); // Right border
  
  // Vertical divider line between left and right sections - left section smaller, right section larger
  doc.line(90, 121, 90, 161); // Vertical divider
  
  // Explicit left and right borders for TAX INVOICE content section
  doc.line(15, 121, 15, 161); // Left border
  doc.line(195, 121, 195, 161); // Right border
  
  // ===== LEFT SECTION: BILL TO / SHIP TO (PROPERLY ALIGNED) =====
  // Section header - 8 pt Bold as specified for column headers
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('BILL TO / SHIP TO', 17, 126);
  
  // Address content - 8 pt Regular as specified for item details
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  yPos = 131; // Adjusted for new position with better spacing
  
  // Customer details with natural text wrapping
  const customerName = invoice.customer.name;
  const customerAddress = invoice.customer.address;
  
  // Customer name with address naturally wrapped
  const fullCustomerInfo = `${customerName} - ${customerAddress}, ${invoice.customer.pincode}, Place of Supply: ${invoice.customer.state}`;
  yPos = wrapText(doc, fullCustomerInfo, 17, yPos, 70, 3.5); // 70mm max width for smaller left section
  
  // ===== RIGHT SECTION: SOLD BY (WITH PROPER PADDING AND NATURAL WRAPPING) =====
  // Section header - 8 pt Normal as specified for column headers
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  // Show seller name if available, otherwise company name
  const sellerName = invoice.company.sellerName || invoice.company.name;
  doc.text(`Sold by : ${sellerName}`, 92, 126);
  
  // Company details - 8 pt Regular as specified for item details
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  yPos = 131; // Adjusted for new position with better spacing
  
  // Format company address with natural text wrapping like in image
  // Show store name if available, otherwise company name
  const displayName = invoice.items[0]?.product?.storeName || invoice.company.storeName || invoice.company.name;
  const companyInfo = `${displayName}, ${invoice.company.address}`;
  yPos = wrapText(doc, companyInfo, 92, yPos, 95, 3.5); // 95mm max width for larger right section
  
  yPos += 2; // Small gap before GSTIN
  doc.setFont('helvetica', 'bold'); // Make GSTIN bold
  doc.text(`GSTIN - ${invoice.company.gstin}`, 92, yPos);
  
  // Invoice details with proper spacing to stay within border
  yPos += 5; // Space before invoice details
  
  const gridStartY = yPos;
  
  doc.setFontSize(8); // Changed from 7 to 8
  doc.setFont('helvetica', 'normal');
  
  // Column headers - positioned for new column layout (right section is larger)
  doc.text('Purchase Order No.', 93, gridStartY);
  doc.text('Invoice No.', 125, gridStartY);
  doc.text('Order Date', 152, gridStartY);
  doc.text('Invoice Date', 175, gridStartY);
  
  // Column values - positioned with proper gap and within border
  doc.setFont('helvetica', 'bold'); // Keep bold
  // Purchase Order Number should NOT have _1 suffix
  const purchaseOrderNumber = invoice.orderNumber.replace('_1', '');
  
  // Ensure dates are properly formatted (DD.MM.YYYY)
  // Check if already in DD.MM.YYYY format (has dots but not ISO format with T)
  const isFormattedDate = (date: string) => {
    return typeof date === 'string' && /^\d{2}\.\d{2}\.\d{4}$/.test(date);
  };
  
  const formattedOrderDate = isFormattedDate(invoice.orderDate as string)
    ? invoice.orderDate 
    : formatDate(new Date(invoice.orderDate));
  const formattedInvoiceDate = isFormattedDate(invoice.invoiceDate as string)
    ? invoice.invoiceDate 
    : formatDate(new Date(invoice.invoiceDate));
  
  doc.text(purchaseOrderNumber, 93, gridStartY + 5);
  doc.text(invoice.invoiceNumber, 125, gridStartY + 5);
  doc.text(formattedOrderDate, 152, gridStartY + 5);
  doc.text(formattedInvoiceDate, 175, gridStartY + 5);
  
  // ===== DESCRIPTION TABLE (POSITIONED AFTER TAX INVOICE) =====
  
  const tableStartY = 161; // Adjusted to start after TAX INVOICE section
  
  // Main table border - outer border only with increased height
  doc.setLineWidth(borderWidth); // 1px border
  doc.rect(15, tableStartY, 180, 60); // Adjusted with better margins: x=15mm, width=180mm
  
  // Table header row with gray background - like image 1
  doc.setFillColor(220, 220, 220); // Light gray background for header
  doc.rect(15, tableStartY, 180, 8, 'F'); // Gray background for table header
  
  doc.setLineWidth(borderWidth); // 1px border
  doc.line(15, tableStartY + 8, 195, tableStartY + 8);
  
  doc.setFillColor(255, 255, 255); // Reset to white
  // Column headers - 8 pt Bold as specified - adjusted for better spacing
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('Description', 17, tableStartY + 5);
  doc.text('HSN', 60, tableStartY + 5);        // Moved left from 68
  doc.text('Qty', 78, tableStartY + 5);        // Moved left from 88
  doc.text('Gross Amount', 88, tableStartY + 5); // Moved left from 98
  doc.text('Discount', 112, tableStartY + 5);   // Moved left from 122
  doc.text('Taxable Value', 128, tableStartY + 5); // Moved left from 140
  doc.text('Taxes', 152, tableStartY + 5);      // Moved left from 160 - more space for IGST
  doc.text('Total', 175, tableStartY + 5);      // Moved left from 178
  
  // NO INTERNAL VERTICAL LINES - Simple layout like image 1
  
  // Calculate totals
  let grandTotal = 0;
  let totalGST = 0;
  
  doc.setFont('helvetica', 'normal');
  let currentY = tableStartY + 12;
  
  // Product row (exactly like image)
  const firstItem = invoice.items[0];
  const totals = calculateTotals(firstItem.product.price, firstItem.quantity, firstItem.product.gstRate, firstItem.discount);
  grandTotal += totals.total;
  totalGST += totals.gstAmount;
  
  // Product description - Split description into lines dynamically
  const maxLineLength = 30; // Characters per line
  const description = firstItem.product.description;
  const words = description.split(' ');
  const descLines: string[] = [];
  let currentLine = '';
  
  words.forEach(word => {
    if ((currentLine + word).length <= maxLineLength) {
      currentLine += (currentLine ? ' ' : '') + word;
    } else {
      if (currentLine) descLines.push(currentLine);
      currentLine = word;
    }
  });
  if (currentLine) descLines.push(currentLine);
  
  // Limit to 6 lines max
  const displayLines = descLines.slice(0, 6);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8); // 8 pt Regular as specified for item details
  displayLines.forEach((line, index) => {
    doc.text(line, 17, currentY + (index * 4)); // Increased spacing from 3 to 4
  });
  
  // Product data - Item details rows 8 pt Regular as specified
  doc.setFontSize(8);
  const productRowY = currentY + 12; // Adjusted to align with new description spacing
  doc.text(firstItem.product.hsn, 60, productRowY); // HSN - moved left
  doc.text(firstItem.quantity.toString(), 80, productRowY); // Qty - moved left
  doc.text(`Rs.${firstItem.product.price.toFixed(2)}`, 88, productRowY); // Gross Amount - moved left
  doc.text(`Rs.${firstItem.discount.toFixed(2)}`, 112, productRowY); // Discount - moved left
  doc.text(`Rs.${totals.taxableValue.toFixed(2)}`, 128, productRowY); // Taxable Value - moved left
  
  // Taxes column (two lines with proper spacing) - more space now
  doc.text(`IGST @${firstItem.product.gstRate.toFixed(1)}%`, 152, productRowY - 2);
  doc.text(`Rs.${totals.gstAmount.toFixed(2)}`, 152, productRowY + 2);
  
  doc.text(`Rs.${totals.total.toFixed(2)}`, 175, productRowY); // Total - moved left
  
  // NO HORIZONTAL LINE AFTER PRODUCT - Simple layout
  
  // Other Charges row - Item details rows 8 pt Regular as specified
  currentY += 30; // Increased spacing to accommodate better description layout
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8); // 8 pt Regular as specified for item details
  doc.text('Other Charges', 17, currentY);
  doc.text('820559', 60, currentY);      // Moved left
  doc.text('NA', 80, currentY);          // Moved left
  doc.text('Rs.0.00', 88, currentY);     // Moved left
  doc.text('Rs.0', 112, currentY);       // Moved left
  doc.text('Rs.0.00', 128, currentY);    // Moved left
  
  // Taxes column (two lines with proper spacing) - more space now
  doc.text('IGST @18.0%', 152, currentY - 2);
  doc.text('Rs.0.00', 152, currentY + 2);
  
  doc.text('Rs.0.00', 175, currentY);    // Moved left
  
  // Total row - Changed to 8pt to match other table content
  currentY += 12; // Better spacing for total row
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8); // Changed from 9 to 8
  doc.text('Total', 17, currentY);
  doc.text(`Rs.${totalGST.toFixed(2)}`, 152, currentY); // Taxes column - moved left
  doc.text(`Rs.${grandTotal.toFixed(2)}`, 175, currentY); // Total column - moved left
  
  // Explicit left and right borders for description table (ensures borders are visible)
  doc.setLineWidth(borderWidth);
  doc.line(15, tableStartY, 15, tableStartY + 60); // Left border
  doc.line(195, tableStartY, 195, tableStartY + 60); // Right border
  
  // ===== FOOTER SECTION (BORDERED BOX LIKE IMAGE 1) =====
  
  // Footer box - extends the main table border
  const footerStartY = tableStartY + 60; // Updated to match new table height
  doc.setLineWidth(borderWidth); // 1px border
  doc.rect(15, footerStartY, 180, 15); // Adjusted with better margins: x=15mm, width=180mm
  
  // Footer text - Tax note 8 pt Regular as specified (Fine print/disclaimer)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8); // Font size 8 as specified
  const footerTextY = footerStartY + 5;
  const footerText = 'Tax is not payable on reverse charge basis. This is a computer generated invoice and does not require signature. Other charges are charges that are applicable to your order and include charges for logistics fee (where applicable). Includes discounts for your city and/or for online payments (as applicable)';
  wrapText(doc, footerText, 17, footerTextY, 175, 3.5); // Wrap text within 175mm width
};